import { createFileRoute } from '@tanstack/react-router'
import BillingPageHeader from '../../Pages/BillingPage/BillingPageHeader'
import PageBody from '../../Pages/Layout/PageBody'
import BillingPage from '../../Pages/BillingPage/BillingPage'
import { processTRPCData } from '../../Queries/fetchData'
import LoadingSpinner from '../../Components/LoadingSpinner'
import ErrorComponent from '../../Components/ErrorComponent'
import {
    canViewRoute,
    migratedToV2,
    usingNewLogin,
} from '../../State/Permissions/HasPermissions'
import SessionStore from '../../State/SessionStore'
import PermissionDenied from '../../Pages/PermissionDenied'
import { CenterPadPageBody } from '@2/layout/page-body'
import { setLayoutDefaults } from '@2/layout/layout-store'
import { trpc } from '@/system/trpc'
import BillingPageStore from '../../Pages/BillingPage/BillingPageStore'
import React, { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

export const Route = createFileRoute('/_private/billing')({
    beforeLoad: ({ params }) => {
        if (!canViewRoute(SessionStore.user, 'billing', params))
            throw new Error('Permission Denied')
        setLayoutDefaults({
            pageTitle: 'Billing',
        })
    },
    errorComponent: ({ error, reset }) => {
        if (error.message === 'Permission Denied') return <PermissionDenied />
        return <ErrorComponent error={error} reset={reset} />
    },
    loader: async () => loader(),
    pendingComponent: LoadingSpinner,
    component: Wrapper,
})

const loader = async () => {
    const billingData = await trpc.legacy.getBillingData.query()

    BillingPageStore.setBillingData(billingData)

    // Ensure SessionStore.subscription has the latest billing period from Stripe
    if (
        billingData.organisationSubscriptions &&
        billingData.organisationSubscriptions.length > 0
    ) {
        const updatedSubscription = billingData.organisationSubscriptions[0]
        if (
            updatedSubscription.subscriptionPeriod &&
            SessionStore.subscription
        ) {
            SessionStore.subscription.subscriptionPeriod =
                updatedSubscription.subscriptionPeriod

            // Also update next payment date if available
            if (updatedSubscription.nextPaymentDate) {
                SessionStore.subscription.nextPaymentDate =
                    updatedSubscription.nextPaymentDate
            }
        }
    }

    const processedModels = processTRPCData(billingData)

    return processedModels
}

function Wrapper() {
    // Handle checkout session completion when returning from Stripe
    useEffect(() => {
        const handleCheckoutReturn = async () => {
            const urlParams = new URLSearchParams(window.location.search)
            const sessionId = urlParams.get('session_id')
            const success = urlParams.get('success')
            const cancelled = urlParams.get('cancelled')
            const type = urlParams.get('type')

            if (sessionId && success === 'true') {
                try {
                    // Track checkout session success
                    const sessionTypeForTracking =
                        type === 'subscription_update'
                            ? 'subscription_update'
                            : type || 'subscription'
                    posthog.capture('checkout_session_success', {
                        sessionId: sessionId,
                        type: sessionTypeForTracking,
                        organisationId: SessionStore.organisation?.id,
                    })
                    // Call our completion handler
                    const completionResult =
                        await trpc.legacy.handleCheckoutCompletion.mutate({
                            sessionId: sessionId,
                        })
                    // Clean up URL parameters
                    const newUrl = new URL(window.location)
                    newUrl.searchParams.delete('session_id')
                    newUrl.searchParams.delete('success')
                    newUrl.searchParams.delete('type')
                    window.history.replaceState({}, '', newUrl.toString())

                    // Reload the page to refresh data
                    window.location.reload()
                } catch (error) {
                    console.error('Error completing checkout session:', error)

                    // Enhanced error categorization for checkout completion failures
                    const errorMessage =
                        error.message ||
                        'Unknown error occurred during checkout completion'
                    const isCouponError =
                        errorMessage.toLowerCase().includes('coupon') ||
                        errorMessage.toLowerCase().includes('validation') ||
                        errorMessage.toLowerCase().includes('discount')
                    const isValidationError =
                        errorMessage.toLowerCase().includes('validation') ||
                        errorMessage
                            .toLowerCase()
                            .includes('transaction validation')

                    // Track checkout session completion failure with error categorization
                    posthog.capture('checkout_session_completion_failed', {
                        sessionId: sessionId,
                        error: errorMessage,
                        errorType: isCouponError
                            ? 'coupon_validation'
                            : isValidationError
                              ? 'transaction_validation'
                              : 'general',
                        organisationId: SessionStore.organisation?.id,
                    })

                    // Enhanced error monitoring with context
                    Sentry.captureException(error, {
                        tags: {
                            operation: 'handleCheckoutSessionCompletion',
                            sessionId: sessionId,
                            errorType: isCouponError
                                ? 'coupon_validation'
                                : isValidationError
                                  ? 'transaction_validation'
                                  : 'general',
                        },
                        extra: {
                            sessionType: type || 'subscription',
                            organisationId: SessionStore.organisation?.id,
                        },
                    })
                    posthog.captureException(error)

                    // Provide specific error messaging based on error type
                    let userMessage =
                        'Checkout completed but there was an issue updating your subscription. Please contact support if this issue persists.'

                    if (isCouponError) {
                        userMessage =
                            'Checkout completed but there was an issue with discount validation. Your payment was processed - please contact support to resolve the discount application.'
                    } else if (isValidationError) {
                        userMessage =
                            'Checkout completed but transaction validation failed. Your payment may have been processed - please contact support to verify your subscription status.'
                    }

                    // Show enhanced user-friendly error message
                    alert(userMessage)
                }
            } else if (cancelled === 'true') {
                // Track checkout session cancellation
                posthog.capture('checkout_session_cancelled', {
                    organisationId: SessionStore.organisation?.id,
                })

                // Clean up URL parameters
                const newUrl = new URL(window.location)
                newUrl.searchParams.delete('cancelled')
                window.history.replaceState({}, '', newUrl.toString())
            }
        }

        handleCheckoutReturn()
    }, [])

    if (migratedToV2()) {
        return (
            <CenterPadPageBody>
                <BillingPage />
            </CenterPadPageBody>
        )
    }

    return (
        <>
            <BillingPageHeader label="Billing" />
            <PageBody>
                <BillingPage />
            </PageBody>
        </>
    )
}
